import React from 'react'
import { Plus, Check } from 'lucide-react'
import { Product } from '../types'

interface ProductGridProps {
  products: Product[]
  onAddToCart: (product: Product) => void
}

const ProductGrid: React.FC<ProductGridProps> = ({ products, onAddToCart }) => {
  const [addedId, setAddedId] = React.useState<string | null>(null)

  const handleAddToCart = (product: Product) => {
    onAddToCart(product)
    setAddedId(product.id)
    setTimeout(() => setAddedId(null), 1000)
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {products.map(product => (
        <div
          key={product.id}
          className="product-card bg-white rounded-lg shadow-sm overflow-hidden transition-all duration-200"
        >
          <div className="aspect-square bg-gray-100 flex items-center justify-center">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x300?text=Product'
                }}
              />
            ) : (
              <div className="text-gray-400 text-4xl">📦</div>
            )}
          </div>

          <div className="p-4">
            <div className="text-xs text-orange-600 font-medium uppercase tracking-wide mb-1">
              {product.category}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-1">
              {product.name}
            </h3>
            <p className="text-gray-600 text-sm mb-3 line-clamp-2">
              {product.description}
            </p>

            <div className="flex items-center justify-between">
              <span className="text-xl font-bold text-gray-900">
                ${product.price.toFixed(2)}
              </span>
              <button
                onClick={() => handleAddToCart(product)}
                disabled={product.stock <= 0}
                className={`flex items-center space-x-1 px-3 py-2 rounded-lg font-medium transition-all ${
                  product.stock <= 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : addedId === product.id
                    ? 'bg-green-500 text-white'
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
              >
                {addedId === product.id ? (
                  <>
                    <Check className="h-4 w-4" />
                    <span>Added</span>
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    <span>Add</span>
                  </>
                )}
              </button>
            </div>

            {product.stock <= 5 && product.stock > 0 && (
              <p className="text-xs text-orange-600 mt-2">
                Only {product.stock} left in stock
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export default ProductGrid
